import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../../app/api";
import { meKey, meQuery } from "../../app/me";
import { endSession } from "../../app/session";
import { outbox } from "../chats/outbox";
import { startDrafts } from "../chats/starts";
import { heldCodes } from "../messaging/api";

/**
 * Drops everything this tab kept for the person whose session ended: its
 * changes in flight, the cache, the replies they were sending, an Edited start's words and doc, and
 * the link codes they were shown (slice 2e).
 */
export function forgetSession(client: QueryClient) {
  // First: a change still in flight from this session aborts, and its late
  // answer writes nothing into the next one's cache (`session.ts`).
  endSession();
  client.clear();
  outbox.clear();
  startDrafts.clear();
  heldCodes.clear();
  client.setQueryData(meKey, null);
}

/**
 * A new session (a login or signup) in this tab: whoever was signed in before
 * may not be this person (their session may have ended in another tab), so
 * nothing kept for them stays, then who's signed in is read again.
 */
export async function startSession(client: QueryClient) {
  forgetSession(client);
  // Not the signed-out null `forgetSession` leaves: it would read as fresh.
  client.removeQueries({ queryKey: meKey });
  await client.prefetchQuery(meQuery);
}

/**
 * Logs out: ends the session, then forgets it (`forgetSession`) and goes to
 * /login. Only once the Worker confirms: until the session is deleted its
 * cookie still signs them in, so a failed call leaves them where they are,
 * with the error to show and retry.
 */
export function useLogOut() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api("POST", "/api/auth/logout"),
    onSuccess: () => {
      forgetSession(client);
      void navigate("/login", { replace: true });
    },
  });
}
